import { exposeWhenVisible, isForced, setTreatmentShare, variantOf } from '@theme/hds-experiments';

/**
 * A/B-test room visualizer «Bekijk in jouw kamer» (SHO-11185).
 *
 * De knop komt uit de app-extensie (`hds-room-visualizer`) en staat standaard verborgen
 * (`snippets/hds-room-visualizer-visibility.liquid`). Dit script zet hem aan door
 * `data-hds-room-visualizer-enabled="true"` op <html> te zetten: bij «Altijd» voor iedereen,
 * bij «A/B-test» alleen in de treatment-arm. In beide armen staan het experiment en de arm op
 * <html> (`data-hds-room-visualizer-experiment` / `-variant`); de extensie stuurt die mee in
 * haar events (`hds:room_visualizer`).
 *
 * De blootstelling telt pas als de knop er zou zijn: de extensie haalt `hidden` van de knop af
 * zodra het product geschikt is (tegel + maten), in béide armen; in control houdt de CSS hem
 * daarna nog verborgen. Telt dus op de plek van de knop (zijn ouder, de galerij), niet op de
 * knop zelf: die is in control nooit zichtbaar en zou control nooit laten meetellen
 * (zelfde valkuil als `vergelijken.js`). Een product zonder tegel telt in geen van beide armen.
 *
 * Instellingen: thema-instellingen → «A/B-test room visualizer» (`room_visualizer_*`),
 * doorgegeven via `<script type="application/json" id="hds-room-visualizer-experiment">`.
 */

const LAUNCHER = '.hds-room-visualizer__launcher';

/** @returns {{ modus: string, id: string, aandeel: number, productId: string } | null} */
function readConfig() {
  try {
    const el = document.getElementById('hds-room-visualizer-experiment');
    return el ? JSON.parse(el.textContent || '{}') : null;
  } catch {
    return null;
  }
}

/**
 * Kiest de arm, zet de knop aan of uit en meldt de blootstelling zodra het product geschikt
 * blijkt. Geeft de arm terug (of `aan`/`uit`), voor tests.
 *
 * @param {ReturnType<typeof readConfig>} config
 * @returns {string}
 */
export function startRoomVisualizerExperiment(config = readConfig()) {
  const html = document.documentElement;
  if (!config || config.modus === 'uit') return 'uit';
  if (config.modus === 'aan') {
    html.dataset.hdsRoomVisualizerEnabled = 'true';
    if (config.material === 'doormat') html.dataset.hdsDoormatEnabled = 'true';
    return 'aan';
  }
  if (config.modus !== 'experiment' || !config.id) return 'uit';

  setTreatmentShare(config.id, config.aandeel);
  const arm = variantOf(config.id);
  html.dataset.hdsRoomVisualizerExperiment = config.id;
  html.dataset.hdsRoomVisualizerVariant = arm;
  // Afgedwongen arm (preview, link voor vrienden en familie) telt niet mee: de extensie labelt haar events als test.
  if (isForced(config.id)) html.dataset.hdsRoomVisualizerForced = 'true';
  if (arm === 'treatment') {
    html.dataset.hdsRoomVisualizerEnabled = 'true';
    if (config.material === 'doormat') html.dataset.hdsDoormatEnabled = 'true';
  }

  let exposed = false;
  const check = () => {
    if (exposed) return;
    const launcher = document.querySelector(LAUNCHER);
    if (!(launcher instanceof HTMLElement) || launcher.hidden || !launcher.parentElement) return;
    exposed = true;
    observer.disconnect();
    exposeWhenVisible(config.id, launcher.parentElement, {
      placement: 'gallery',
      product_id: config.productId ?? '',
    });
  };
  const observer = new MutationObserver(check);
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden'] });
  check();
  return arm;
}

startRoomVisualizerExperiment();
